/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function initSitePageLoader() {
  var loader = document.getElementById('site-page-loader');
  if (!loader) {
    return;
  }

  var removed = false;
  // ポイント履歴ページ（data-dokopoi-point-apps あり）なら Dokopoi 完了までローダーを残す
  var waitForDokopoi = !!document.querySelector('[data-dokopoi-point-apps]');

  function complete() {
    if (removed) {
      return;
    }
    removed = true;

    document.documentElement.classList.add('page-is-loaded');
    loader.setAttribute('aria-hidden', 'true');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // モーション軽減時はフェードなしで即隠す
      loader.style.transition = 'none';
      loader.style.opacity = '0';
      loader.style.visibility = 'hidden';
      loader.setAttribute('hidden', '');
      return;
    }

    var hide = function onTransitionEnd(e) {
      if (e && e.propertyName && e.propertyName !== 'opacity') {
        return;
      }
      loader.removeEventListener('transitionend', hide);
      loader.setAttribute('hidden', '');
    };
    loader.addEventListener('transitionend', hide);
    setTimeout(function () {
      if (!loader.hasAttribute('hidden')) {
        hide();
      }
    }, 400);
  }

  // Dokopoi 側からローダー解除を呼ぶフック（ポイントページ以外は null）
  window.__completeSitePageLoaderForDokopoi = waitForDokopoi ? complete : null;

  // ポイントページ: 早期 complete せず Dokopoi 完了を待つ
  if (waitForDokopoi) {
    // bfcache 復元時はローダーが残らないよう解除
    window.addEventListener(
      'pageshow',
      function (event) {
        if (event.persisted) {
          complete();
        }
      },
      { passive: true }
    );
    return;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', complete, { once: true });
  } else {
    requestAnimationFrame(complete);
  }

  window.addEventListener(
    'pageshow',
    function (event) {
      if (event.persisted) {
        complete();
      }
    },
    { passive: true }
  );
})();

/* Swym ウィッシュリスト描画は assets/swym-wishlist-grid.js に移行 */

/**
 * ポイント履歴ページ: Dokopoi 描画完了まで全画面ローダーを残し、揃ったらフェードで消す。
 */
(function initDokopoiPointAppsReveal() {
  // ポイントページの Apps ラッパー（無いページでは何もしない）
  var apps = document.querySelector('[data-dokopoi-point-apps]');
  // 対象外なら即終了
  if (!apps) {
    return;
  }

  // 完了処理を一度だけ走らせるためのフラグ
  var completed = false;
  // DOM 変化監視用（完了時に disconnect する）
  var observer = null;
  // 描画が落ち着いてから消すための遅延タイマー
  var settleTimer = null;
  // MutationObserver のコールバックを rAF で間引くためのフラグ
  var scheduled = false;
  // Liquid の customer 有無（ログイン済なら true）
  var expectLoggedIn = apps.hasAttribute('data-dokopoi-expect-logged-in');
  // 未ログイン完了判定用の見出し（apps.liquid が Dokopoi ブロック設定から付与）
  var loginMidashi = apps.getAttribute('data-dokopoi-login-midashi') || '';
  // 未ログインは customer → history と段階描画されるため長めに待つ
  var settleMs = expectLoggedIn ? 200 : 900;

  // Dokopoi 側の UI が「見せてよい状態」か判定する
  function isReady() {
    // ログイン済: 保有ポイント数が出ていれば OK
    if (expectLoggedIn) {
      // Dokopoi が描画する保有ポイント要素
      var remaining = apps.querySelector('#dokopoiRemainingPoint');
      // 要素があり、空白以外の文字が入っていること
      return !!(remaining && remaining.textContent.replace(/\s/g, '').length > 0);
    }
    // 未ログイン: 履歴ブロックのログイン CTA が描画されてから
    // （customer ブロックだけの途中段階では消さない）
    // ログイン CTA ボタンの有無を確認する
    var hasLoginButton = !!apps.querySelector('#dokopoiLoginButtonId');
    // ボタンがまだ無い＝履歴ブロック未描画なので待つ
    if (!hasLoginButton) {
      // まだ ready ではない
      return false;
    }
    // 見出し設定がある場合は本文一致も要求（テーマ設定変更に追従）
    if (loginMidashi) {
      // Apps 配下のテキスト全体を取得する
      var text = apps.textContent || '';
      // ブロック設定の見出しが描画本文に含まれていれば ready
      return text.indexOf(loginMidashi) !== -1;
    }
    // 見出しを渡せなかった場合はボタン出現のみで判定
    return true;
  }

  // 監視を止め、全画面ローダーのフェード解除を依頼する
  function complete() {
    // 二重実行防止
    if (completed) {
      return;
    }
    completed = true;
    // DOM 監視を停止
    if (observer) {
      observer.disconnect();
    }
    // 残っている settle タイマーをクリア
    if (settleTimer) {
      window.clearTimeout(settleTimer);
    }
    // サイト共通ローダーの complete を呼ぶ（initSitePageLoader 側のフック）
    if (typeof window.__completeSitePageLoaderForDokopoi === 'function') {
      window.__completeSitePageLoaderForDokopoi();
    }
  }

  // ready なら settleMs 後に complete、未 ready なら待ちタイマーを取り消す
  function tryComplete() {
    // まだ途中描画なら、進行中の settle を止めて待つ
    if (!isReady()) {
      if (settleTimer) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }
      return;
    }
    // ready 後に追加 mutation が来たらタイマーを引き直す（デバウンス）
    if (settleTimer) {
      window.clearTimeout(settleTimer);
    }
    // 描画が settleMs 静かならローダーを消す
    settleTimer = window.setTimeout(complete, settleMs);
  }

  // 初回時点ですでに描画済みなら、そのまま settle 待ちに入る
  if (isReady()) {
    settleTimer = window.setTimeout(complete, settleMs);
  }

  // Dokopoi の非同期描画を監視する
  observer = new MutationObserver(function () {
    // 同一フレーム内の連続通知はまとめる
    if (scheduled) {
      return;
    }
    scheduled = true;
    // レイアウト反映後に判定する
    window.requestAnimationFrame(function () {
      scheduled = false;
      tryComplete();
    });
  });
  // 子要素追加・テキスト変化をツリー全体で監視
  observer.observe(apps, { childList: true, subtree: true, characterData: true });

  // 描画失敗時もローダーが残り続けないための保険（5秒）
  window.setTimeout(complete, 5000);
})();

/**
 * ハンバーガーメニュー内の同一ページ遷移（/#pittari-product-search など）で
 * ドロワーが閉じず半透明オーバーレイが残る問題を防ぐ。
 * フルページ遷移では再読込で消えるが、ハッシュのみの遷移では open が残るため明示的に閉じる。
 */
(function closeMobileMenuOnInDrawerLinkClick() {
  document.addEventListener(
    'click',
    function (event) {
      var link = event.target.closest && event.target.closest('a[href]');
      if (!link) {
        return;
      }

      var drawer = document.getElementById('mobile-menu-drawer');
      if (!drawer || !drawer.open || !drawer.contains(link)) {
        return;
      }

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#' && href.length === 1) {
        return;
      }

      // 同一ドキュメント内（パス同一＋ハッシュ）またはハッシュのみのリンクで閉じる。
      // 他ページへの遷移は再読込で消えるが、閉じても害はないので同様に閉じる。
      drawer.open = false;
    },
    true
  );
})();

(function alignReviewBody() {
  var listSelector = '#judgeme_product_reviews .jdgm-review-list';
  var bodySelector = '.jdgm-review-content__body-content';
  var frame = null;
  var expandedCard = null;
  var expandTimer = null;

  function align() {
    frame = null;

    document.querySelectorAll(listSelector).forEach(function (list) {
      var rows = new Map();
      var cards = Array.from(list.querySelectorAll('.jm-review-item'));

      cards.forEach(function (card) {
        var body = card.querySelector(bodySelector);
        if (body) body.style.removeProperty('margin-block-start');
      });

      if (window.innerWidth < 1000) return;

      cards.forEach(function (card) {
        var body = card.querySelector(bodySelector);
        if (!body) return;

        var rowTop = Math.round(card.getBoundingClientRect().top);
        if (!rows.has(rowTop)) rows.set(rowTop, []);
        rows.get(rowTop).push(body);
      });

      rows.forEach(function (bodies) {
        var tops = bodies.map(function (body) {
          return body.getBoundingClientRect().top;
        });
        var targetTop = Math.max.apply(null, tops);

        bodies.forEach(function (body, index) {
          var gap = targetTop - tops[index];
          if (gap > 1) {
            var margin = parseFloat(getComputedStyle(body).marginBlockStart) || 0;
            body.style.marginBlockStart = margin + gap + 'px';
          }
        });
      });
    });
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(align);
  }

  function watchReviewLists() {
    var lists = document.querySelectorAll(listSelector);
    reviewObserver.disconnect();
    if (!lists.length) {
      reviewObserver.observe(document.body, { childList: true, subtree: true });
      return;
    }

    lists.forEach(function (list) {
      reviewObserver.observe(list, {
        childList: true,
        subtree: true,
        characterData: true
      });
      if (list.parentElement) {
        reviewObserver.observe(list.parentElement, { childList: true });
      }
    });
    schedule();
  }

  var reviewObserver = new MutationObserver(function (records) {
    if (expandedCard && records.every(function (record) {
      return expandedCard.contains(record.target);
    })) return;
    watchReviewLists();
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.jm-review-content__expand-button');
    if (!button) return;

    expandedCard = button.closest('.jm-review-item');
    window.clearTimeout(expandTimer);
    expandTimer = window.setTimeout(function () {
      expandedCard = null;
    }, 500);
  }, true);

  watchReviewLists();

  window.addEventListener('resize', schedule);
  schedule();
})();
